export const DEFAULT_MAX_ATTEMPTS = 8
export const DEFAULT_INTERVAL_MS = 2000

function stripTrailingSlash (url) {
  return url.replace(/\/$/, '')
}

/**
 * @param {string} origin
 * @returns {boolean}
 */
export function isLocalDevWebOrigin (origin) {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin)
}

/**
 * Public web app base URL (parity with cloud-functions reportAppURL).
 * Returns null when the canonical URL is not configured (fail closed on Capacitor / non-dev hosts).
 * @param {string} [baseUrl]
 * @returns {string|null}
 */
export function resolveInvitationBaseUrl (baseUrl) {
  if (baseUrl) {
    return stripTrailingSlash(baseUrl)
  }
  const configured = process.env.VUE_APP_REPORT_APP_URL
  if (configured) {
    return stripTrailingSlash(configured)
  }
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const origin = window.location.origin
    const isCapacitor = origin.startsWith('capacitor:')
    const isDevWeb = process.env.NODE_ENV === 'development' && isLocalDevWebOrigin(origin)
    if (!isCapacitor && isDevWeb) {
      console.warn(
        '[invitation-link] VUE_APP_REPORT_APP_URL is unset; using window.location.origin for invitation URLs.'
      )
      return stripTrailingSlash(origin)
    }
  }
  return null
}

/**
 * @param {{ isNewUser?: boolean, userToken?: string }} fields
 * @returns {string}
 */
export function buildCampaignInvitationPath ({ isNewUser, userToken } = {}) {
  if (isNewUser === true && userToken) {
    return `welcome/${encodeURIComponent(userToken)}`
  }
  return 'organizationHome'
}

/**
 * @param {{ isNewUser?: boolean, userToken?: string, baseUrl?: string }} options
 * @returns {string}
 */
export function buildCampaignInvitationUrl ({ isNewUser, userToken, baseUrl } = {}) {
  const base = resolveInvitationBaseUrl(baseUrl)
  if (!base) {
    return null
  }
  const path = buildCampaignInvitationPath({ isNewUser, userToken })
  return `${base}/${path}`
}

/**
 * @param {{ exists: boolean, data?: Object }} userDoc
 * @returns {{ invitationKind: 'new'|'existing'|'unknown', userToken: string|null }}
 */
export function parseUserInvitationFields (userDoc) {
  if (!userDoc.exists) {
    return { invitationKind: 'unknown', userToken: null }
  }
  const data = userDoc.data || {}
  if (data.isNewUser === true) {
    return {
      invitationKind: 'new',
      userToken: data.userToken || null
    }
  }
  return {
    invitationKind: 'existing',
    userToken: data.userToken || null
  }
}

/**
 * @param {{ invitationKind: string, userToken: string|null }} fields
 * @param {{ baseUrl: string }} context
 * @returns {{ url: string, isNewUser: boolean, userToken: string|null, ready: true }|null}
 */
export function resolveInvitationFromFields (fields, { baseUrl }) {
  if (fields.invitationKind === 'existing') {
    const url = buildCampaignInvitationUrl({ isNewUser: false, baseUrl })
    if (!url) {
      return null
    }
    return {
      url,
      isNewUser: false,
      userToken: fields.userToken,
      ready: true
    }
  }
  if (fields.invitationKind === 'new' && fields.userToken) {
    const url = buildCampaignInvitationUrl({
      isNewUser: true,
      userToken: fields.userToken,
      baseUrl
    })
    if (!url) {
      return null
    }
    return {
      url,
      isNewUser: true,
      userToken: fields.userToken,
      ready: true
    }
  }
  return null
}

/**
 * @param {() => Promise<{ invitationKind: string, userToken: string|null }>} fetchFields
 * @param {{ maxAttempts?: number, intervalMs?: number, baseUrl?: string, expectNewUser?: boolean, sleep?: (ms: number) => Promise<void> }} [options]
 * @returns {Promise<{ url: string|null, isNewUser: boolean, userToken: string|null, ready: boolean }>}
 */
export async function waitForInvitationUrlWithFetcher (fetchFields, options = {}) {
  const maxAttempts = options.maxAttempts != null ? options.maxAttempts : DEFAULT_MAX_ATTEMPTS
  const intervalMs = options.intervalMs != null ? options.intervalMs : DEFAULT_INTERVAL_MS
  const expectNewUser = options.expectNewUser === true
  const resolvedBaseUrl = resolveInvitationBaseUrl(options.baseUrl)
  const sleepFn = options.sleep || defaultSleep

  if (!resolvedBaseUrl) {
    return {
      url: null,
      isNewUser: false,
      userToken: null,
      ready: false,
      baseUrlMissing: true
    }
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const fields = await fetchFields()
    const resolved = resolveInvitationFromFields(fields, {
      baseUrl: resolvedBaseUrl
    })
    if (resolved) {
      return resolved
    }

    if (attempt < maxAttempts - 1) {
      await sleepFn(intervalMs)
    }
  }

  return {
    url: null,
    isNewUser: expectNewUser,
    userToken: null,
    ready: false
  }
}

function defaultSleep (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}



// WEBPACK FOOTER //
// ./src/lib/invitation-link.core.js